import React, { useMemo, useCallback } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useSidebar } from '@atlassian/jira-business-app-wrapper';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';

const CONDITIONAL_FORMATTING_SIDEBAR_CONTENT_KEY = 'conditionalFormattingSidebar';

export type ConditionalFormattingSidebarProps = {
	onCloseSidebar: (
		e: React.MouseEvent<HTMLElement, MouseEvent>,
		analyticsEvent: UIAnalyticsEvent,
	) => void;
};

export const useConditionalFormattingSidebar = ({
	SidebarComponent,
}: {
	SidebarComponent: React.FC<ConditionalFormattingSidebarProps>;
}) => {
	const [{ content }, { setSidebarContent }] = useSidebar();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isConditionalFormattingSidebarOpen = useMemo(
		() => content?.key === CONDITIONAL_FORMATTING_SIDEBAR_CONTENT_KEY,
		[content],
	);

	const onCloseSidebar = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({}),
			'button clicked',
			'conditionalFormattingSidebarButton',
			{
				open: false,
			},
		);
		setSidebarContent(null);
	}, [createAnalyticsEvent, setSidebarContent]);

	const onClickConditionalFormattingButton = useCallback(() => {
		if (isConditionalFormattingSidebarOpen) {
			onCloseSidebar();
		} else {
			fireUIAnalytics(
				createAnalyticsEvent({}),
				'button clicked',
				'conditionalFormattingSidebarButton',
				{
					open: true,
				},
			);
			setSidebarContent(
				<SidebarComponent
					key={CONDITIONAL_FORMATTING_SIDEBAR_CONTENT_KEY}
					onCloseSidebar={onCloseSidebar}
				/>,
			);
		}
	}, [
		SidebarComponent,
		createAnalyticsEvent,
		isConditionalFormattingSidebarOpen,
		onCloseSidebar,
		setSidebarContent,
	]);

	return {
		onClickConditionalFormattingButton,
		isConditionalFormattingSidebarOpen,
	};
};
